import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { sendEmail, emailTemplates } from '../../lib/emailService';
import VasePreview from '../common/VasePreview';

const IMG_BLANK = '/images/arizona-roses-blank-card.png';

const ORDERED_STATUSES = [
  'pending',
  'paid',
  'out_for_delivery',
  'delivered',
  'cancelled',
];

const STATUS_LABELS = {
  pending:          'Order Received',
  paid:             'Payment Confirmed',
  out_for_delivery: 'Out for Delivery',
  delivered:        'Hand Delivered',
  cancelled:        'Cancelled',
};

const STATUS_COLORS = {
  pending:          'bg-yellow-50 text-yellow-700 border-yellow-100',
  paid:             'bg-green-50 text-green-700 border-green-100',
  out_for_delivery: 'bg-teal-50 text-teal-700 border-teal-100',
  delivered:        'bg-sage-50 text-sage-700 border-sage-100',
  cancelled:        'bg-red-50 text-red-600 border-red-100',
};

export default function OrderDrawer({ order: initialOrder, isOpen, onClose, onUpdate }) {
  const [order, setOrder] = useState(initialOrder);
  const [uploading, setUploading] = useState(false);
  const [uploadErrorMsg, setUploadErrorMsg] = useState('');
  const [photos, setPhotos] = useState([]);
  const [captureError, setCaptureError] = useState('');
  const [captureSuccess, setCaptureSuccess] = useState(false);
  const [stickerImageUrl, setStickerImageUrl] = useState('');
  const [stickerName, setStickerName] = useState('');
  const fileInputRef = useRef();
  const isUploadingRef = useRef(false);

  useEffect(() => {
    if (initialOrder && isOpen && !isUploadingRef.current) {
      setOrder(initialOrder);
      setCaptureError('');
      setCaptureSuccess(false);
      setUploadErrorMsg('');
      fetchPhotos(initialOrder.id);
      // Fetch sticker image by ID if URL wasn't saved at order time
      const item0 = initialOrder.order_items?.[0] || {};
      if (item0.card_design) {
        if (item0.card_design_image_url) {
          setStickerImageUrl(item0.card_design_image_url);
          setStickerName('');
        } else if (isSupabaseConfigured()) {
          supabase
            .from('card_icons_1739812345678')
            .select('image_url, name')
            .eq('id', item0.card_design)
            .single()
            .then(({ data }) => {
              if (data) {
                setStickerImageUrl(data.image_url || '');
                setStickerName(data.name || '');
              }
            });
        }
      } else {
        setStickerImageUrl('');
        setStickerName('');
      }
    }
  }, [initialOrder, isOpen]);

  async function fetchPhotos(orderId) {
    if (!orderId || !isSupabaseConfigured()) return;
    const { data } = await supabase
      .from('order_photos')
      .select('*')
      .eq('order_id', orderId)
      .order('uploaded_at', { ascending: false });
    if (data) setPhotos(data);
  }

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !order || !isSupabaseConfigured()) return;

    const item = order.order_items?.[0] || {};
    setUploading(true);
    isUploadingRef.current = true;
    setUploadErrorMsg('');

    try {
      if (photos.length > 0) {
        const existingPhoto = photos[0];
        const oldPath = existingPhoto.photo_url.split('/arrangement-photos/')[1];
        if (oldPath) await supabase.storage.from('arrangement-photos').remove([oldPath]);
        await supabase.from('order_photos').delete().eq('id', existingPhoto.id);
      }

      const fileExt = file.name.split('.').pop();
      const filePath = `arrangements/${order.id}/${Date.now()}.${fileExt}`;

      const { error: storageError } = await supabase.storage
        .from('arrangement-photos')
        .upload(filePath, file);
      if (storageError) throw storageError;

      const { data: { publicUrl } } = supabase.storage
        .from('arrangement-photos')
        .getPublicUrl(filePath);

      const { data: newPhoto, error: dbError } = await supabase
        .from('order_photos')
        .insert({ order_id: order.id, photo_url: publicUrl })
        .select()
        .single();
      if (dbError) throw dbError;

      const { error: statusError } = await supabase
        .from('orders')
        .update({ status: 'out_for_delivery' })
        .eq('id', order.id);
      if (statusError) throw statusError;

      setPhotos([newPhoto]);
      setOrder(prev => ({ ...prev, status: 'out_for_delivery' }));
      isUploadingRef.current = false;
      onUpdate();

      try {
        if (order.customer_email) {
          await sendEmail(
            order.customer_email,
            `Your roses are on their way! — Arizona Roses`,
            emailTemplates.outForDelivery(order, publicUrl, item.design_name || 'Custom Arrangement')
          );
        }
      } catch (emailErr) {
        console.error("Failed to send photo ready email:", emailErr);
      }
    } catch (err) {
      console.error('Photo upload sequence failed:', err);
      isUploadingRef.current = false;
      setUploadErrorMsg(err.message || 'Upload failed. Please check your connection and try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!order || !isSupabaseConfigured()) return;
    try {
      setOrder(prev => ({ ...prev, status: newStatus }));
      await supabase.from('orders').update({ status: newStatus }).eq('id', order.id);
      onUpdate();

      // ── Trigger customer emails on key status transitions ─────────────────
      if (newStatus === 'out_for_delivery' && order.customer_email) {
        const item = order.order_items?.[0] || {};
        const photoUrl = photos[0]?.photo_url || null;
        try {
          await sendEmail(
            order.customer_email,
            `Your roses are on their way! — Arizona Roses`,
            emailTemplates.outForDelivery(order, photoUrl, item.design_name || 'Custom Arrangement')
          );
        } catch (emailErr) {
          console.error('Failed to send out-for-delivery email:', emailErr);
        }
      }

      if (newStatus === 'delivered' && order.customer_email) {
        try {
          await sendEmail(
            order.customer_email,
            `Delivered with love — Arizona Roses`,
            emailTemplates.orderDelivered(order)
          );
        } catch (emailErr) {
          console.error('Failed to send delivered email:', emailErr);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRefund = async () => {
    if (!window.confirm('Are you sure you want to cancel this order? This cannot be undone.')) return;

    try {
      await supabase
        .from('orders')
        .update({ status: 'cancelled' })
        .eq('id', order.id);

      setOrder(prev => ({ ...prev, status: 'cancelled' }));
      onUpdate();
      alert('Order marked as cancelled. If a refund is needed, process it in your Stripe dashboard.');
    } catch (err) {
      alert('Failed to cancel order: ' + err.message);
    }
  };

  const handleContactCustomer = () => {
    if (order?.customer_email) {
      window.location.href = `mailto:${order.customer_email}?subject=Your Arizona Roses Order - #${order.id.substring(0, 8)}`;
    }
  };

  if (!order) return null;
  const item = order.order_items?.[0] || {};
  const currentPhoto = photos[0] || null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-charcoal/40 backdrop-blur-sm z-[60]" />
          <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} className="fixed right-0 top-0 h-full w-full max-w-2xl bg-warmwhite shadow-2xl z-[70] overflow-y-auto">
            <div className="p-6 md:p-8 lg:p-10 space-y-8">
              <div className="flex justify-between items-start">
                <div>
                  <span className="text-xs font-bold text-blush-400 uppercase tracking-widest block mb-1">Admin Order View</span>
                  <h2 className="text-3xl font-serif text-charcoal">#{order.id.substring(0, 8)}</h2>
                </div>
                <button onClick={onClose} className="w-11 h-11 flex items-center justify-center bg-white border border-sage-100 hover:bg-sage-50 rounded-full text-sage-400 transition-all shadow-sm active:scale-90">
                  <SafeIcon name="X" className="text-xl" />
                </button>
              </div>

              {/* Status */}
              <div className="glass-panel p-6 space-y-4">
                <label className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Update Order Status</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
                  <select
                    className="input-field !min-h-0 !py-3 !text-sm font-bold !rounded-full"
                    value={order.status}
                    onChange={(e) => handleStatusUpdate(e.target.value)}
                  >
                    {ORDERED_STATUSES.map(status => (
                      <option key={status} value={status}>{STATUS_LABELS[status]}</option>
                    ))}
                  </select>
                  <div className={`px-4 py-2 rounded-full border text-[10px] font-bold uppercase tracking-wider text-center ${STATUS_COLORS[order.status]}`}>
                    Current: {STATUS_LABELS[order.status]}
                  </div>
                </div>
              </div>

              {/* Arrangement Specs */}
              <div className="glass-panel p-6 space-y-4">
                <h3 className="font-serif text-xl border-b border-sage-50 pb-2">Arrangement</h3>
                <div className="flex flex-col sm:flex-row gap-6 items-start">
                  {/* VasePreview with rose color overlay — falls back to static image */}
                  <div className="w-full sm:w-32 shrink-0">
                    {item.design_id && item.color_preference ? (
                      <div className="w-32 h-32 rounded-3xl overflow-hidden border border-sage-100 shadow-sm">
                        <VasePreview
                          design={{ id: item.design_id, name: item.design_name, image_url: item.design_image_url }}
                          roseColor={item.color_preference}
                        />
                      </div>
                    ) : (
                      <div className="w-32 h-32 rounded-3xl overflow-hidden bg-sage-50 border border-sage-100">
                        <img src={item.design_image_url || order.design_image_url} className="w-full h-full object-cover" alt="Vase" />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-4 flex-1">
                    <div>
                      <p className="text-[10px] font-bold text-sage-400 uppercase">Design</p>
                      <p className="text-sm font-semibold text-charcoal">{item.design_name || 'Custom'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-sage-400 uppercase">Size</p>
                      <p className="text-sm font-semibold text-charcoal">{item.size}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-sage-400 uppercase">Color</p>
                      <p className="text-sm font-semibold text-charcoal">{item.color_preference}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-sage-400 uppercase">Price</p>
                      <p className="text-sm font-bold text-blush-500">${item.price}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Add-ons & Card */}
              {(item.addons || item.card_from_name) && (
                <div className="glass-panel p-6 space-y-4">
                  <h3 className="font-serif text-xl border-b border-sage-50 pb-2">Add-ons & Card</h3>

                  {/* Add-on thumbnails */}
                  {(() => {
                    let addons = [];
                    try { addons = item.addons ? JSON.parse(item.addons) : []; } catch {}
                    return addons.length > 0 ? (
                      <div>
                        <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest mb-3">Perfect Pairings</p>
                        <div className="flex flex-wrap gap-3">
                          {addons.map(addon => (
                            <div key={addon.id} className="flex flex-col items-center gap-1.5 w-20">
                              <div className="w-20 h-20 rounded-2xl bg-warmwhite border border-sage-100 overflow-hidden flex items-center justify-center p-2 shadow-sm">
                                {addon.image ? (
                                  <img src={addon.image} alt={addon.name} className="w-full h-full object-contain" />
                                ) : (
                                  <SafeIcon name="Gift" className="text-sage-300 text-2xl" />
                                )}
                              </div>
                              <span className="text-[10px] font-medium text-sage-500 text-center leading-tight">{addon.name}</span>
                              <span className="text-[10px] font-bold text-blush-400">${addon.price}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {/* Card preview */}
                  {item.card_from_name && (
                    <div>
                      <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest mb-3">Gift Card</p>
                      <div className="flex items-start gap-5">
                        <div className="relative w-24 shadow-md rounded-sm overflow-hidden bg-white aspect-[3/4] ring-2 ring-white shrink-0">
                          <img src={IMG_BLANK} alt="Card" className="w-full h-full object-cover block" />
                          {stickerImageUrl && (
                            <div className="absolute flex items-center justify-center" style={{ top: '10%', left: '50%', transform: 'translateX(-50%)', width: '60%', height: '45%' }}>
                              <img src={stickerImageUrl} alt="Sticker" className="w-full h-full object-contain drop-shadow-md" />
                            </div>
                          )}
                          <div
                            className="absolute font-serif font-bold text-charcoal w-full text-center px-2 leading-tight break-words"
                            style={{ bottom: '10%', fontSize: item.card_from_name?.length > 14 ? '0.5rem' : '0.7rem' }}
                          >
                            {item.card_from_name}
                          </div>
                        </div>
                        <div className="space-y-2 pt-1">
                          <div>
                            <p className="text-[10px] font-bold text-sage-400 uppercase">Signed by</p>
                            <p className="text-sm font-semibold text-charcoal">{item.card_from_name}</p>
                          </div>
                          {(stickerName || item.card_design) && (
                            <div>
                              <p className="text-[10px] font-bold text-sage-400 uppercase">Sticker</p>
                              <p className="text-sm font-semibold text-charcoal capitalize">{stickerName || '—'}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Customer & Delivery */}
              <div className="glass-panel p-6 space-y-6">
                <h3 className="font-serif text-xl border-b border-sage-50 pb-2">Customer & Delivery</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div>
                      <p className="text-[10px] font-bold text-sage-400 uppercase">Customer</p>
                      <p className="text-sm text-charcoal font-semibold">{order.customer_name}</p>
                      <p className="text-xs text-sage-500">{order.customer_email}</p>
                      <p className="text-xs text-sage-500">{order.customer_phone}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-sage-400 uppercase">Recipient</p>
                      <p className="text-sm text-charcoal font-semibold">{order.recipient_name}</p>
                      <p className="text-xs text-sage-500">{order.recipient_phone}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-[10px] font-bold text-sage-400 uppercase">Delivery To</p>
                      <p className="text-sm text-charcoal">{order.delivery_address}</p>
                      <p className="text-xs text-sage-500">{order.delivery_city}, {order.delivery_zip}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-sage-400 uppercase">Delivery Date</p>
                      <p className="text-sm font-bold text-charcoal">{new Date(order.delivery_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
                {order.special_instructions && (
                  <div className="bg-sage-50/50 p-5 rounded-[2rem] border border-sage-100">
                    <p className="text-[10px] font-bold text-sage-400 uppercase mb-1">Special Instructions</p>
                    <p className="text-xs text-sage-600 italic leading-relaxed">"{order.special_instructions}"</p>
                  </div>
                )}
              </div>

              {/* Payment */}
              <div className="glass-panel p-6 space-y-4">
                <h3 className="font-serif text-xl border-b border-sage-50 pb-2">Payment</h3>

                {order.stripe_payment_intent_id ? (
                  <div className="space-y-4">
                    <div className="bg-sage-50 rounded-2xl p-4 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Amount</span>
                        <span className="font-bold text-charcoal text-lg">
                          ${order.stripe_amount_cents ? (order.stripe_amount_cents / 100).toFixed(2) : '—'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Status</span>
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          order.status === 'cancelled' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'
                        }`}>
                          {order.status === 'cancelled' ? 'Cancelled' : 'Paid'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Payment ID</span>
                        <span className="text-[10px] text-sage-400 font-mono">{order.stripe_payment_intent_id?.substring(0, 20)}...</span>
                      </div>
                    </div>

                    {order.status !== 'cancelled' && (
                      <button
                        onClick={handleRefund}
                        className="btn-secondary !border-red-200 !text-red-500 hover:!bg-red-50 w-full flex items-center justify-center gap-2"
                      >
                        <SafeIcon name="X" /> Cancel Order
                      </button>
                    )}
                    <p className="text-[10px] text-sage-400 font-medium">
                      To issue a refund, cancel the order here then process the refund in your Stripe dashboard.
                    </p>
                  </div>
                ) : (
                  <div className="bg-sage-50 rounded-2xl p-4 text-center space-y-2">
                    <SafeIcon name="CreditCard" className="text-3xl text-sage-300 mx-auto" />
                    <p className="text-sm text-sage-400">No payment on file for this order.</p>
                  </div>
                )}
              </div>

              {/* Arrangement Photo */}
              <div className="glass-panel p-6 space-y-4">
                <div className="flex justify-between items-center border-b border-sage-50 pb-2">
                  <div>
                    <h3 className="font-serif text-xl">Arrangement Photo</h3>
                    {currentPhoto && (
                      <p className="text-[10px] text-sage-400 font-medium mt-0.5">
                        Uploading a new photo will replace the current one
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleContactCustomer}
                      className="btn-secondary !py-2 !px-4 !text-xs"
                      title="Email customer about this order"
                    >
                      <SafeIcon name="Mail" /> Contact
                    </button>
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="btn-secondary">
                      {uploading ? 'Uploading...' : currentPhoto ? 'Replace Photo' : 'Add Photo'}
                    </button>
                  </div>
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileUpload} />
                </div>

                {uploadErrorMsg && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-2xl border border-red-100 text-[10px] font-bold uppercase tracking-wider flex items-center gap-2">
                    <SafeIcon name="AlertCircle" />
                    {uploadErrorMsg}
                  </div>
                )}

                {currentPhoto ? (
                  <div className="space-y-2">
                    <div className="aspect-square rounded-[1.5rem] overflow-hidden border border-sage-100 bg-sage-50 shadow-sm max-w-sm mx-auto">
                      <img src={currentPhoto.photo_url} className="w-full h-full object-cover" alt="Arrangement" />
                    </div>
                    <p className="text-[10px] text-sage-400 font-medium text-center">
                      Uploaded {new Date(currentPhoto.uploaded_at).toLocaleString()}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-sage-400 text-center py-8 italic">
                    No photo uploaded yet. Add one to notify the customer.
                  </p>
                )}
              </div>

            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
